/**
 * Authentication Context
 * Provides global authentication state and methods
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Check for existing authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if user has tokens stored (check both access and refresh tokens)
        const hasToken = authService.isAuthenticated();
        const refreshToken = localStorage.getItem('refreshToken');

        if (hasToken || refreshToken) {
          // Load cached user immediately for instant UI
          const cachedUser = authService.getStoredUser();
          if (cachedUser) {
            setUser(cachedUser);
          }

          // Fetch fresh user data from server
          try {
            const freshUser = await authService.getCurrentUser();
            setUser(freshUser);
          } catch (err) {
            console.error('Failed to fetch user:', err);

            // If we have cached user but fetch fails, keep the cached user
            // The API service will handle token refresh automatically
            // Only logout if explicitly unauthorized
            if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
              await authService.logout();
              setUser(null);
            } else if (!cachedUser) {
              // No cached user and fetch failed
              await authService.logout();
              setUser(null);
            }
          }
        } else {
          // No tokens - user is not logged in
          setUser(null);
        }
      } catch (err) {
        console.error('Auth init error:', err);
        setError(err.message);
        await authService.logout();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError('');

      const result = await authService.login(email, password);

      // Check if user has admin role
      const userRole = result.user.role?.name || result.user.role;
      const isAdmin = ['Admin', 'Super Admin', 'ADMIN', 'SUPER_ADMIN', 'Moderator', 'MODERATOR'].includes(userRole);

      if (!isAdmin) {
        await authService.logout();
        throw new Error('Access denied. Admin privileges required.');
      }

      setUser(result.user);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
    // Also update localStorage
    const stored = authService.getStoredUser();
    if (stored) {
      localStorage.setItem('user', JSON.stringify({ ...stored, ...userData }));
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const hasToken = authService.isAuthenticated();
    const refreshToken = localStorage.getItem('refreshToken');

    if (!hasToken && !refreshToken) {
      setUser(null);
      return false;
    }

    try {
      const freshUser = await authService.getCurrentUser();
      setUser(freshUser);
      return true;
    } catch (err) {
      // If we have cached user, keep them logged in
      const cachedUser = authService.getStoredUser();
      if (cachedUser) {
        setUser(cachedUser);
        return true;
      }
      setUser(null);
      return false;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    updateUser,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
